/**
 * The sending animation of the request forms (owner, 2026-11-04), called by assets/theme.js
 * before WhatsApp opens: the form card folds into a small card, becomes a cut diamond that
 * sparkles, drops into an open jewellery box, the lid closes and the box flies to the WhatsApp
 * button of the bar (or to the top corner). Returns a promise resolved at the end. With reduced
 * motion the card only fades. Drawn in a fixed layer above the page and removed afterwards.
 */
(() => {
  window.theme = window.theme || {};

  const DIAMOND = `
    <svg viewBox="0 0 64 56" aria-hidden="true">
      <defs>
        <linearGradient id="SendDiamondFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#ffffff"/>
          <stop offset="0.55" stop-color="#f3ead8"/>
          <stop offset="1" stop-color="#d9c49a"/>
        </linearGradient>
      </defs>
      <path d="M13 3h38l11 15-30 35L2 18z" fill="url(#SendDiamondFill)" stroke="#b59d69" stroke-width="1.6" stroke-linejoin="round"/>
      <path d="M2 18h60M13 3l8 15M32 3l-11 15M32 3l11 15M51 3l-8 15M21 18l11 35M43 18l-11 35" fill="none" stroke="#b59d69" stroke-width="1.1" stroke-linejoin="round"/>
    </svg>`;

  const BOX = `
    <svg viewBox="0 0 120 92" aria-hidden="true">
      <g class="form-send__lid">
        <rect x="10" y="16" width="100" height="30" rx="9" fill="#2b241c"/>
        <rect x="10" y="40" width="100" height="6" fill="#b59d69"/>
      </g>
      <rect x="10" y="46" width="100" height="42" rx="9" fill="#1e1913"/>
      <rect x="10" y="46" width="100" height="5" fill="#b59d69"/>
      <rect x="34" y="52" width="52" height="14" rx="7" fill="#3a3027"/>
    </svg>`;

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  /* The card comes back once the box has left: a form in the page fades in while the store
     answers (or shows its spam check); a dialog is already closed and stays closed. */
  const reveal = (card) => {
    card.style.visibility = '';
    if (card instanceof HTMLDialogElement || !card.animate) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    card.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 400, easing: 'ease-out' });
  };
  const done = (animation) => (animation && animation.finished ? animation.finished.catch(() => {}) : Promise.resolve());

  window.theme.sendAnimation = (card, rect) => {
    if (!(card instanceof Element) || !Element.prototype.animate) return Promise.resolve();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const from = rect || card.getBoundingClientRect();
    if (reduced) {
      card.style.visibility = 'hidden';
      return wait(150).then(() => reveal(card));
    }

    const layer = document.createElement('div');
    layer.className = 'form-send';
    layer.setAttribute('aria-hidden', 'true');
    layer.innerHTML = `<div class="form-send__veil"></div><div class="form-send__card"></div><div class="form-send__diamond">${DIAMOND}<span class="form-send__spark"></span><span class="form-send__spark"></span><span class="form-send__spark"></span></div><div class="form-send__box">${BOX}</div>`;
    document.body.appendChild(layer);

    const veil = layer.querySelector('.form-send__veil');
    const ghost = layer.querySelector('.form-send__card');
    const diamond = layer.querySelector('.form-send__diamond');
    const box = layer.querySelector('.form-send__box');
    const lid = layer.querySelector('.form-send__lid');
    const sparks = layer.querySelectorAll('.form-send__spark');

    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const ease = 'cubic-bezier(0.65, 0, 0.35, 1)';
    Object.assign(ghost.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` });
    // Hidden while its ghost travels; shown again at the end (a form in the page fades back in).
    card.style.visibility = 'hidden';

    const run = async () => {
      veil.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 250, fill: 'forwards' });

      // 1. The card folds into a small card in the middle of the screen.
      const size = 120;
      await done(ghost.animate([
        { transform: 'translate(0, 0) scale(1, 1)', borderRadius: '28px' },
        { transform: `translate(${cx - from.left - from.width / 2}px, ${cy - from.top - from.height / 2}px) scale(${size / from.width}, ${size / from.height})`, borderRadius: `${(28 * from.width) / size}px` },
      ], { duration: 520, easing: ease, fill: 'forwards' }));

      // 2. The small card becomes a cut diamond, which sparkles.
      Object.assign(diamond.style, { left: `${cx}px`, top: `${cy}px` });
      ghost.animate([{ opacity: 1, transform: ghost.style.transform }, { opacity: 0 }], { duration: 260, fill: 'forwards' });
      await done(diamond.animate([
        { opacity: 0, transform: 'translate(-50%, -50%) scale(0.4) rotate(-20deg)' },
        { opacity: 1, transform: 'translate(-50%, -50%) scale(1.08) rotate(4deg)', offset: 0.7 },
        { opacity: 1, transform: 'translate(-50%, -50%) scale(1) rotate(0)' },
      ], { duration: 420, easing: 'ease-out', fill: 'forwards' }));
      sparks.forEach((spark, index) => {
        spark.animate([
          { opacity: 0, transform: 'scale(0.2) rotate(0deg)' },
          { opacity: 1, transform: 'scale(1) rotate(45deg)', offset: 0.5 },
          { opacity: 0, transform: 'scale(0.2) rotate(90deg)' },
        ], { duration: 520, delay: index * 120, easing: 'ease-in-out' });
      });

      // 3. The open box rises under it; the diamond drops in and the lid closes.
      const boxY = cy + 70;
      Object.assign(box.style, { left: `${cx}px`, top: `${boxY}px` });
      await done(box.animate([
        { opacity: 0, transform: 'translate(-50%, -30%)' },
        { opacity: 1, transform: 'translate(-50%, -50%)' },
      ], { duration: 320, easing: 'ease-out', fill: 'forwards' }));
      await wait(160);
      // The diamond sinks into the box: it is gone before the lid closes over it.
      diamond.getAnimations().forEach((animation) => animation.cancel());
      await done(diamond.animate([
        { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
        { opacity: 1, transform: `translate(-50%, calc(-50% + ${boxY - cy - 14}px)) scale(0.42)`, offset: 0.8 },
        { opacity: 0, transform: `translate(-50%, calc(-50% + ${boxY - cy - 6}px)) scale(0.36)` },
      ], { duration: 460, easing: 'cubic-bezier(0.55, 0, 1, 0.45)', fill: 'forwards' }));
      await done(lid.animate([
        { transform: 'rotate(-112deg)' },
        { transform: 'rotate(6deg)', offset: 0.8 },
        { transform: 'rotate(0deg)' },
      ], { duration: 360, easing: 'ease-in', fill: 'forwards' }));

      // 4. The closed box flies to the WhatsApp button of the bar, or to the top corner.
      const target = document.querySelector('.site-header__whatsapp');
      const t = target && target.offsetParent ? target.getBoundingClientRect() : null;
      const rtl = document.documentElement.dir === 'rtl';
      const tx = t ? t.left + t.width / 2 : (rtl ? 40 : window.innerWidth - 40);
      const ty = t ? t.top + t.height / 2 : 40;
      veil.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 600, delay: 150, fill: 'forwards' });
      await done(box.animate([
        { transform: 'translate(-50%, -50%) scale(1) rotate(0)', opacity: 1 },
        { transform: `translate(calc(-50% + ${(tx - cx) * 0.35}px), calc(-50% + ${(ty - boxY) * 0.2 - 60}px)) scale(0.75) rotate(-8deg)`, opacity: 1, offset: 0.35 },
        { transform: `translate(calc(-50% + ${tx - cx}px), calc(-50% + ${ty - boxY}px)) scale(0.18) rotate(-18deg)`, opacity: 0.2 },
      ], { duration: 680, easing: 'cubic-bezier(0.5, 0, 0.75, 0)', fill: 'forwards' }));
    };

    // A tab left in the background slows the animation down: the sending never waits past 4.5 s.
    return Promise.race([run(), wait(4500)]).finally(() => {
      layer.remove();
      reveal(card);
    });
  };
})();
